import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import { SUBMISSION_STATUSES, INITIAL_FORM_STATE } from '../constants/researchSupport';
import { enrichCloudinaryFileMeta } from '../utils/cloudinaryDelivery';
import { assertCanSubmitApplication, assertCanSaveDraft } from '../utils/researchRateLimit';
import { createAdminNotificationMeta } from '../utils/adminNotificationMeta';

const COLLECTION = 'research_submissions';
const DRAFTS_COLLECTION = 'research_submission_drafts';
const LOGS_COLLECTION = 'activityLogs';

function normalizeTitle(title) {
  return (title || '').trim().toLowerCase();
}

/** Firestore rejects `undefined`; strip it recursively from draft/submission payloads. */
function sanitizeForFirestore(value) {
  if (value === undefined) return undefined;
  if (value === null) return null;
  if (Array.isArray(value)) {
    return value
      .map((item) => sanitizeForFirestore(item))
      .filter((item) => item !== undefined);
  }
  if (typeof value === 'object' && value.constructor === Object) {
    const out = {};
    Object.entries(value).forEach(([key, val]) => {
      const cleaned = sanitizeForFirestore(val);
      if (cleaned !== undefined) out[key] = cleaned;
    });
    return out;
  }
  return value;
}

function sanitizeUploadedFile(file) {
  if (!file || typeof file !== 'object' || !file.url) return null;
  const isPdf =
    file.format === 'pdf' || file.originalName?.toLowerCase().endsWith('.pdf');
  const enriched = enrichCloudinaryFileMeta(file, { isPdf });
  const out = {
    url: enriched.url,
    originalName: enriched.originalName || '',
  };
  if (enriched.publicId) out.publicId = enriched.publicId;
  if (enriched.bytes != null) out.bytes = enriched.bytes;
  if (enriched.format) out.format = enriched.format;
  if (enriched.resourceType) out.resourceType = enriched.resourceType;
  if (enriched.viewUrl) out.viewUrl = enriched.viewUrl;
  if (enriched.previewUrl) out.previewUrl = enriched.previewUrl;
  if (enriched.downloadUrl) out.downloadUrl = enriched.downloadUrl;
  return out;
}

function sanitizeFiles(files) {
  if (!files || typeof files !== 'object') return {};
  const out = {};
  ['thesisPdf', 'supportingDocs', 'dataset'].forEach((key) => {
    const cleaned = sanitizeUploadedFile(files[key]);
    if (cleaned) out[key] = cleaned;
  });
  return out;
}

export async function loadDraft(userId) {
  const draftRef = doc(db, DRAFTS_COLLECTION, userId);
  const snap = await getDoc(draftRef);
  if (!snap.exists()) return { ...INITIAL_FORM_STATE, currentStep: 0 };
  const data = snap.data();
  return {
    ...INITIAL_FORM_STATE,
    ...data.form,
    files: {
      ...INITIAL_FORM_STATE.files,
      ...sanitizeFiles(data.form?.files),
    },
    currentStep: data.currentStep ?? 0,
    draftUpdatedAt: data.updatedAt,
  };
}

export async function saveDraft(userId, form, currentStep) {
  await assertCanSaveDraft(userId);
  const draftRef = doc(db, DRAFTS_COLLECTION, userId);
  const formPayload = sanitizeForFirestore({
    ...form,
    files: sanitizeFiles(form.files),
  });
  await setDoc(
    draftRef,
    {
      userId,
      form: formPayload,
      currentStep,
      updatedAt: new Date().toISOString(),
    },
    { merge: true }
  );
}

export async function checkDuplicateSubmission(userId, researchTitle) {
  const normalized = normalizeTitle(researchTitle);
  const q = query(
    collection(db, COLLECTION),
    where('userId', '==', userId),
    where('titleNormalized', '==', normalized)
  );
  const snap = await getDocs(q);
  const active = snap.docs.filter((d) => {
    const status = d.data().status;
    return status !== SUBMISSION_STATUSES.REJECTED && status !== SUBMISSION_STATUSES.DRAFT;
  });
  return active.length > 0;
}

export async function logActivity({ submissionId, userId, action, details, actorEmail }) {
  await addDoc(collection(db, LOGS_COLLECTION), {
    submissionId: submissionId || null,
    userId: userId || null,
    action,
    details: details || {},
    actorEmail: actorEmail || null,
    createdAt: serverTimestamp(),
    module: 'research_support',
  });
}

export async function submitResearchApplication({ userId, userEmail, form }) {
  await assertCanSubmitApplication(userId);
  const isDuplicate = await checkDuplicateSubmission(userId, form.researchTitle);
  if (isDuplicate) {
    throw new Error(
      'You have already submitted a project with this research title. Contact support if you need to update it.'
    );
  }

  const submissionRef = doc(collection(db, COLLECTION));
  const now = new Date().toISOString();

  const applicantName = form.fullName.trim();
  const payload = {
    userId,
    applicantEmail: userEmail,
    fullName: applicantName,
    email: form.email.trim(),
    phone: form.phone.trim(),
    institution: form.institution.trim(),
    department: form.department.trim(),
    levelOfStudy: form.levelOfStudy,
    country: form.country.trim(),
    graduationYear: form.graduationYear,
    researchTitle: form.researchTitle.trim(),
    titleNormalized: normalizeTitle(form.researchTitle),
    researchField: form.researchField,
    abstract: form.abstract.trim(),
    keywords: form.keywords.trim(),
    publicationGoal: form.publicationGoal,
    linkedIn: form.linkedIn?.trim() || '',
    files: sanitizeFiles(form.files),
    status: SUBMISSION_STATUSES.SUBMITTED,
    timeline: [
      {
        status: SUBMISSION_STATUSES.SUBMITTED,
        note: 'Application received',
        at: now,
      },
    ],
    reviewerNotes: '',
    assignedReviewerIds: [],
    createdAt: now,
    updatedAt: now,
    submittedAt: now,
    ...createAdminNotificationMeta({
      type: 'research_submission',
      summary: `New research submission from ${applicantName}`,
    }),
  };

  await setDoc(submissionRef, sanitizeForFirestore(payload));

  await logActivity({
    submissionId: submissionRef.id,
    userId,
    action: 'submission_created',
    details: { researchTitle: payload.researchTitle },
    actorEmail: userEmail,
  });

  const draftRef = doc(db, DRAFTS_COLLECTION, userId);
  await setDoc(draftRef, { clearedAt: now }, { merge: true });

  return { id: submissionRef.id, ...payload };
}

export async function fetchUserSubmissions(userId) {
  const q = query(
    collection(db, COLLECTION),
    where('userId', '==', userId),
    orderBy('createdAt', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function fetchSubmissionById(submissionId) {
  const ref = doc(db, COLLECTION, submissionId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

export async function fetchAllSubmissions() {
  const q = query(collection(db, COLLECTION), orderBy('createdAt', 'desc'));
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

export async function updateSubmissionStatus({
  submissionId,
  status,
  note,
  reviewerNotes,
  actorEmail,
  notifyApplicant = true,
}) {
  const ref = doc(db, COLLECTION, submissionId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Submission not found');

  const existing = snap.data();
  const now = new Date().toISOString();
  const timelineEntry = {
    status,
    note: note || `Status updated to ${status}`,
    at: now,
    by: actorEmail || 'admin',
  };

  const updates = {
    status,
    updatedAt: now,
    timeline: [...(existing.timeline || []), timelineEntry],
  };
  if (reviewerNotes !== undefined) {
    updates.reviewerNotes = reviewerNotes;
  }
  updates.notifyApplicant = notifyApplicant;
  updates.lastAdminNote = note || '';

  await updateDoc(ref, sanitizeForFirestore(updates));

  await logActivity({
    submissionId,
    userId: existing.userId,
    action: 'status_updated',
    details: { status, note },
    actorEmail,
  });

  return { id: submissionId, ...existing, ...updates };
}

export async function updatePendingSubmission(submissionId, userId, form) {
  const ref = doc(db, COLLECTION, submissionId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Submission not found');

  const data = snap.data();
  if (data.userId !== userId) throw new Error('Unauthorized');
  if (![SUBMISSION_STATUSES.DRAFT, SUBMISSION_STATUSES.REVISION_REQUESTED].includes(data.status)) {
    throw new Error('This submission can no longer be edited');
  }

  const now = new Date().toISOString();
  await updateDoc(
    ref,
    sanitizeForFirestore({
      ...form,
      researchTitle: form.researchTitle.trim(),
      titleNormalized: normalizeTitle(form.researchTitle),
      updatedAt: now,
      files: form.files ? sanitizeFiles(form.files) : data.files,
    })
  );
}
